// app.js — MS-2401H browser instrument panel: wires the machine wrapper to the
// renderers, input mapper, audio, and facia controls.
//
// Structure:
//   ui/machine.js  — bridge wrapper + pacing loop + turbo boot + polling
//   ui/crt.js      — external Hercules/CRT renderer (interleaved VRAM decode)
//   ui/lcd.js      — built-in 640x80 LCD monitor renderer (linear)
//   ui/printer.js  — the platen: live page bitmap + animated carriage head
//   ui/input.js    — KeyboardEvent.code -> PC set-1 make code -> matrix
//   ui/audio.js    — beeper as a gated WebAudio square wave
// app.js just orchestrates and owns DOM plumbing.

import { Machine } from './ui/machine.js';
import { CrtRenderer } from './ui/crt.js';
import { LcdRenderer } from './ui/lcd.js';
import { PrinterPlaten } from './ui/printer.js';
import { InputMapper } from './ui/input.js';
import { SoftKeyboard } from './ui/softkbd.js';
import { Beeper } from './ui/audio.js';
import { ModalDialog } from './ui/help.js';
import { DiskManager } from './ui/diskman.js';

const $ = (id) => document.getElementById(id);
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const params = new URLSearchParams(location.search);

const el = {
  machine: $('machine'),
  errorToast: $('error-toast'), errorMsg: $('error-msg'),
  crtOff: $('crt-off'),
  crtModeLabel: $('crt-mode-label'),
  crtPhosphor: $('crt-phosphor'),
  leds: [$('led-0'), $('led-1'), $('led-2')], powerLed: $('led-3'),
  powerBtn: $('btn-power'),
  resetBtn: $('btn-reset'),
  pauseBtn: $('btn-pause'), pauseGlyph: $('pause-glyph'),
  displayExpandBtn: $('btn-display-expand'), displayExpandLabel: $('display-expand-label'),
  tearBtn: $('btn-tear'),
  printDrawer: $('print-drawer'),
  printDrawerToggle: $('print-drawer-toggle'),
  printDrawerPanel: $('print-drawer-panel'),
  printDrawerPreview: $('print-drawer-preview'),
  printDrawerEmpty: $('print-drawer-empty'),
  printDrawerTear: $('print-drawer-tear'),
  printDrawerFeed: $('print-drawer-feed'),
  printDrawerState: $('print-drawer-state'),
  diskManagerBtn: $('btn-disk-manager'), diskOverlay: $('disk-overlay'),
  diskDialogBody: $('disk-dialog-body'), diskPath: $('disk-path'),
  diskList: $('disk-list'), diskFoot: $('disk-foot'), diskNote: $('disk-note'),
  diskUpload: $('disk-upload'), diskUploadFile: $('disk-upload-file'),
  diskMkdir: $('disk-mkdir'), diskRefresh: $('disk-refresh'), diskBusyLed: $('disk-busy-led'),
  knobUp: $('knob-up'), knobDown: $('knob-down'), knobFeed: $('knob-feed'),
  platenStatus: $('platen-status'),
  loadBtn: $('btn-load'), saveBtn: $('btn-save'), floppyFile: $('floppy-file'), fddLed: $('fdd-led'),
  muteBtn: $('btn-mute'), muteLabel: $('mute-label'), volume: $('volume'),
  kbdBtn: $('btn-kbd'),
  captureLed: $('capture-led'), captureLabel: $('capture-label'), captureHint: $('capture-hint'),
  status: $('status-readout'),
  screenRegion: $('screen-region'),
};

const machine = new Machine();
const crt = new CrtRenderer($('crt'));
const lcd = new LcdRenderer($('lcd'));
const printer = new PrinterPlaten({
  viewport: $('paper-viewport'), scaler: $('paper-scaler'), surface: $('paper-surface'),
  overlay: $('carriage-rail'), head: $('print-head'), empty: $('paper-empty'),
  status: $('platen-status'), followBtn: $('follow-btn'),
}, reduceMotion);
printer.bindMachine(machine);
const beeper = new Beeper();
const input = new InputMapper(machine, el.screenRegion);
new ModalDialog({
  trigger: $('btn-help'),
  overlay: $('help-overlay'),
  background: el.machine,
  onOpen: () => input.setCaptured(false),
});

let activeDisplay = 'lcd';
let enlargedDisplay = null;
let lastDisplaySnapshot = null;

function displayName(kind) { return kind === 'crt' ? 'CRT' : '液晶'; }

function updateDisplayExpandAction() {
  if (!el.displayExpandBtn || !el.displayExpandLabel) return;
  const enlarged = enlargedDisplay !== null;
  const target = enlarged ? enlargedDisplay : activeDisplay;
  el.displayExpandBtn.setAttribute('aria-pressed', String(enlarged));
  el.displayExpandLabel.textContent = enlarged ? '还原显示' : `放大${displayName(target)}`;
  const action = enlarged ? '还原' : '放大';
  el.displayExpandBtn.setAttribute('aria-label', `${action}${displayName(target)}显示`);
  el.displayExpandBtn.title = enlarged
    ? `还原${displayName(target)}显示`
    : `放大当前使用中的${displayName(target)}显示`;
}

function setActiveDisplay(kind) {
  if (kind !== 'crt') kind = 'lcd';
  if (activeDisplay === kind) return;
  activeDisplay = kind;
  el.screenRegion.dataset.activeDisplay = kind;
  if (enlargedDisplay === null) updateDisplayExpandAction();
}

function repaintDisplaysAfterLayout() {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (!lastDisplaySnapshot || !machine.powered) return;
    const info = lastDisplaySnapshot.display;
    if (info.mode & 2) crt.render(machine, info);
    lcd.render(machine);
  }));
}

function setDisplayEnlarged(on) {
  const enlarged = !!on;
  if (enlarged === (enlargedDisplay !== null)) return;
  if (enlarged) {
    enlargedDisplay = activeDisplay;
    el.machine.dataset.enlargedDisplay = enlargedDisplay;
  } else {
    enlargedDisplay = null;
    delete el.machine.dataset.enlargedDisplay;
  }
  el.machine.classList.toggle('is-display-enlarged', enlarged);
  updateDisplayExpandAction();
  repaintDisplaysAfterLayout();
}

el.screenRegion.dataset.activeDisplay = activeDisplay;
updateDisplayExpandAction();

// On-screen keyboard: the ONLY way to reach the MS-2401H's Chinese
// word-processor keys (拷屏 / 取消命令 / 编码拼音 …). Mounted as the last grid
// row of .machine so opening it compresses the display column rather than
// covering the platen. Drives the matrix directly, so it works with or without
// PC-keyboard capture.
const softkbd = new SoftKeyboard(machine);
$('machine').appendChild(softkbd.el);

// ---- audio unlock on first user gesture --------------------------------
let audioUnlocked = false;
function unlockAudio() {
  if (audioUnlocked) return;
  audioUnlocked = beeper.unlock();
}
['pointerdown', 'keydown'].forEach((ev) =>
  window.addEventListener(ev, unlockAudio, { once: false, passive: true }));

// ---- machine error handling --------------------------------------------
machine.onError((msg) => {
  el.errorToast.classList.remove('is-hidden');
  el.errorMsg.textContent = msg;
});

// ---- per-frame render ---------------------------------------------------
let lastSnapWall = 0;
let lastDrawerPreviewWall = 0;
machine.onFrame((m, snap) => {
  lastDisplaySnapshot = snap;
  const now = performance.now();
  // External CRT: only present when it reports video+graphics AND has non-blank
  // VRAM; otherwise the "no signal" plate shows (the welcome banner lives on
  // the built-in LCD monitor).
  const gfx = (snap.display.mode & 1) !== 0;
  const video = (snap.display.mode & 2) !== 0;
  let crtLit = 0;
  if (video) crtLit = crt.render(m, snap.display);
  // Treat a near-empty framebuffer (a few stray bits) as "no signal" so the CRT
  // reads as an intentional idle plate rather than dead black at the welcome
  // screen (the active display there is the built-in LCD monitor).
  const crtOn = video && gfx && crtLit > 64;
  el.crtOff.classList.toggle('is-hidden', crtOn);
  setActiveDisplay(crtOn ? 'crt' : 'lcd');

  // Bezel label: LIVE CRTC geometry + mode kind, mirroring the same "is there
  // actually something to look at" test as the NO SIGNAL plate above (a
  // programmed-but-blank mode, e.g. the boot welcome screen where the guest's
  // Hercules VRAM is still all zero, reads as "no signal" here too).
  el.crtModeLabel.textContent = crtOn
    ? `${snap.display.width}×${snap.display.height} · ${gfx ? '图形' : '文本'}`
    : '';

  // Built-in LCD monitor.
  lcd.render(m);

  // Printer platen: draw any drained pages + move the carriage head.
  printer.render(m, snap.head);
  const hasPrintedTape = printer.hasPage();
  el.tearBtn.disabled = !hasPrintedTape;
  if (el.printDrawerTear) el.printDrawerTear.disabled = !hasPrintedTape;
  if (el.printDrawerState) el.printDrawerState.textContent = el.platenStatus.textContent || '待机';
  if (el.printDrawer?.classList.contains('is-open') && now - lastDrawerPreviewWall > 500) {
    updatePrintDrawerPreview();
    lastDrawerPreviewWall = now;
  }

  // Live preview: while the head is active, ask for a fresh snapshot ~2Hz.
  const active = snap.head.valid && (snap.head.busy || snap.head.firing);
  if (active && now - lastSnapWall > 500) {
    m.requestSnapshot();
    lastSnapWall = now;
  }

  // Guest outputs drive 磁盘 / 大写 / 数字. 电源 follows the web front-panel
  // latch, with the explicit boot/off handlers covering times when this loop
  // is stopped.
  for (let i = 0; i < el.leds.length; i++) el.leds[i].classList.toggle('is-on', !!snap.leds[i]);
  el.powerLed.classList.toggle('is-on', machine.powered);
  el.fddLed.classList.toggle('is-on', !!snap.leds[0]);

  // Beeper.
  beeper.update(snap.beeper.enabled, snap.beeper.freq, snap.beeper.events, snap.beeper.eventFreq);

  // User-facing state only; low-level timing/paint telemetry belongs in debug
  // hooks, not on the word processor's front panel.
  el.status.textContent = m.paused ? '暂停' : (m.booting ? '启动中…' : '运行');

  diskman.onFrame(snap);
});

// ---- boot ---------------------------------------------------------------
// Fires whenever the machine reaches realtime after a turbo-boot settle: the
// initial boot AND every power-on cold boot / RESET warm restart. So it is the
// single place that re-enables the front-panel controls that were disabled
// during the re-initialisation.
machine.onBooted(() => {
  el.powerBtn.classList.add('is-on');
  el.powerBtn.setAttribute('aria-pressed', 'true');
  el.powerLed.classList.add('is-on');
  el.resetBtn.disabled = false;
  el.pauseBtn.disabled = false;
  el.displayExpandBtn.disabled = false;
  setKnobEnabled(true);   // 走纸 knob is usable once the machine is up
  // A fresh boot always comes up running: normalise the pause button visual.
  el.pauseBtn.setAttribute('aria-pressed', 'false');
  el.pauseGlyph.textContent = 'II';
  el.pauseBtn.classList.remove('is-active');
  el.status.textContent = '运行';
  // Power-on twin of powerDown()'s refresh: clear the drawer's 电源已关闭 note
  // and re-list the disk without a manual refresh (only when the popup is open).
  if (diskman.isOpen) diskman.refresh();
  // ?autoprint=1 presses the print key after boot.
  if (params.get('autoprint') === '1') {
    setTimeout(() => machine.pressPrintKey(), 2000);
  }
  // ?softprint=1 drives 拷屏 through the soft keyboard press/release path.
  if (params.get('softprint') === '1') {
    setTimeout(() => softkbd.softKey('拷屏'), 2000);
  }
});

async function boot() {
  try {
    // No loading screen: the facia is simply present. The displays stay dark /
    // 无信号 until the guest paints — that IS the loading state. A module-load
    // failure (the only thing worth surfacing) goes to the 机器故障 toast.
    await machine.load();
    setKnobEnabled(true);
    machine.start();
  } catch (e) {
    el.errorToast.classList.remove('is-hidden');
    el.errorMsg.textContent = String(e && e.message ? e.message : e);
    console.error('[ms2401] boot failed', e);
  }
}

// ---- controls -----------------------------------------------------------

el.displayExpandBtn.addEventListener('click', () => {
  setDisplayEnlarged(enlargedDisplay === null);
});

// Pause / resume the machine clock.
el.pauseBtn.addEventListener('click', () => {
  if (machine.paused) { machine.resume(); }
  else { machine.pause(); }
  const paused = machine.paused;
  el.pauseBtn.setAttribute('aria-pressed', String(paused));
  el.pauseGlyph.textContent = paused ? '▶' : 'II';
  el.pauseBtn.classList.toggle('is-active', paused);
  el.status.textContent = paused ? '暂停' : (machine.booting ? '启动中…' : '运行');
});

// ---- POWER (电源) — a real latching on/off switch -----------------------
// OFF: stop the emulation loop, physically dark the displays (dead tube + unlit
// LCD, NOT the "no signal" plate), silence the beeper, release keyboard capture.
// The platen keeps its paper (physical). ON from OFF: a full cold boot by
// dropping and rebuilding the wasm module (machine.powerOn), turbo boot intact.
function powerDown() {
  setDisplayEnlarged(false);
  setActiveDisplay('lcd');
  machine.powerOff();          // halts the pacing loop (no more onFrame)
  crt.powerOff();              // dead black glass
  lcd.powerOff();              // bare unlit reflective panel
  el.crtOff.classList.add('is-hidden');   // tube is OFF, not merely "no signal"
  el.crtModeLabel.textContent = '';
  beeper.update(false, 0);     // kill any sounding tone (loop won't gate it now)
  input.setCaptured(false);    // release the keyboard
  for (const led of el.leds) led.classList.remove('is-on');  // lamps dark
  el.powerLed.classList.remove('is-on');
  el.fddLed.classList.remove('is-on');
  el.machine.classList.add('is-powered-off');
  el.powerBtn.classList.remove('is-on');
  el.powerBtn.setAttribute('aria-pressed', 'false');
  el.pauseBtn.disabled = true;
  el.resetBtn.disabled = true;
  el.displayExpandBtn.disabled = true;
  setKnobEnabled(false);   // no analog paper feed while powered off
  diskman.refresh();       // drop the stale listing -> shows 电源已关闭
  el.status.textContent = '关机';
}

async function powerUp() {
  el.machine.classList.remove('is-powered-off');
  el.powerBtn.classList.add('is-on');
  el.powerBtn.setAttribute('aria-pressed', 'true');
  el.powerLed.classList.add('is-on');
  el.pauseBtn.disabled = true;
  el.resetBtn.disabled = true;
  el.displayExpandBtn.disabled = false;
  el.status.textContent = '启动中…';
  el.crtModeLabel.textContent = '';
  try {
    // Cold boot with no web chrome: dark displays until the guest paints its own
    // boot sequence. onBooted re-enables CPU controls once turbo-boot settles;
    // the mechanical platen feed comes back as soon as the printer device exists.
    await machine.powerOn();
    setKnobEnabled(true);
  } catch (e) {
    el.powerLed.classList.remove('is-on');
    el.errorToast.classList.remove('is-hidden');
    el.errorMsg.textContent = String(e && e.message ? e.message : e);
    console.error('[ms2401] power-on failed', e);
  }
}

el.powerBtn.addEventListener('click', () => {
  if (machine.powered) powerDown();
  else powerUp();
});

// ---- RESET (复位) — warm restart via the hardware RESET line -------------
// Momentary. Disabled while the re-init/turbo-boot is in flight (double-click
// guard); onBooted re-enables it. No powered-off interlude and NO web chrome —
// the machine simply reboots and the guest redraws its own welcome banner; the
// only cue is the #status-readout instrument text.
el.resetBtn.addEventListener('click', () => {
  if (!machine.powered || el.resetBtn.disabled) return;
  el.resetBtn.disabled = true;
  el.pauseBtn.disabled = true;
  el.status.textContent = '复位中…';
  const ok = machine.reset();
  if (!ok) {                   // not ready / already resetting: undo the UI lock
    el.resetBtn.disabled = false;
    el.pauseBtn.disabled = false;
  }
});

// ---- CRT type menu (显像管) — one display-type choice, persisted ----------
// 像素 is a display TYPE (flat panel, clean pixels), not an option on a tube.
const CRT_PREF = { phosphor: 'ms2401.crt.phosphor', legacyCrisp: 'ms2401.crt.crisp' };
function loadCrtPrefs() {
  let ph = 'blue';
  try {
    ph = localStorage.getItem(CRT_PREF.phosphor) || 'blue';
    // Migrate the retired crisp checkbox pref: crisp-on becomes the 像素 type.
    if (localStorage.getItem(CRT_PREF.legacyCrisp) === '1') ph = 'pixel';
    localStorage.removeItem(CRT_PREF.legacyCrisp);
  } catch (_) { /* private mode: fall back to defaults */ }
  crt.setPhosphor(ph);
  if (el.crtPhosphor) el.crtPhosphor.value = crt.phosphor;
}
loadCrtPrefs();

el.crtPhosphor && el.crtPhosphor.addEventListener('change', () => {
  crt.setPhosphor(el.crtPhosphor.value);
  try { localStorage.setItem(CRT_PREF.phosphor, crt.phosphor); } catch (_) {}
});

// Tear off -> export the whole strip since the last tear, then physically tear.
el.tearBtn.addEventListener('click', () => {
  printer.exportPng();
  updatePrintDrawerPreview();
});
if (el.printDrawerTear) {
  el.printDrawerTear.addEventListener('click', () => {
    printer.exportPng();
    updatePrintDrawerPreview();
  });
}
if (el.printDrawerToggle) {
  el.printDrawerToggle.addEventListener('click', () => {
    const open = !el.printDrawer.classList.contains('is-open');
    el.printDrawer.classList.toggle('is-open', open);
    el.printDrawerToggle.setAttribute('aria-expanded', String(open));
    if (open) {
      machine.requestSnapshot();
      updatePrintDrawerPreview();
    }
  });
}

const diskman = new DiskManager(machine, {
  trigger: el.diskManagerBtn, overlay: el.diskOverlay, background: el.machine,
  body: el.diskDialogBody, onOpen: () => input.setCaptured(false),
  path: el.diskPath, list: el.diskList, foot: el.diskFoot, note: el.diskNote,
  uploadBtn: el.diskUpload, uploadFile: el.diskUploadFile,
  mkdirBtn: el.diskMkdir, refreshBtn: el.diskRefresh, busyLed: el.diskBusyLed,
});

function updatePrintDrawerPreview() {
  if (!el.printDrawerPreview) return;
  lastDrawerPreviewWall = performance.now();
  const hasPreview = printer.renderPreview(el.printDrawerPreview);
  el.printDrawerPreview.classList.toggle('is-empty', !hasPreview);
  el.printDrawerEmpty?.classList.toggle('is-hidden', hasPreview);
}

// ---- 走纸 platen knob — analog paper repositioning ----------------------
// The knob rolls the paper by hand on the SAME LF accumulator the motor feeds
// (machine.knob -> vm86_printer_knob), so the platen viewport just follows. A
// detent is ±2 LF (one dot row); Shift, or holding past the initial delay,
// steps ±48 LF (one printed line) and the hold accelerates. ▲ feeds forward
// (margin gap); ▼ rolls back (overprint — new ink merges over the old rows).
const KNOB_FINE_LF = 2;    // one dot row (pin pitch = 2 LF steps)
const KNOB_COARSE_LF = 48; // one pass / printed line
function knobRoll(sign, magnitude) {
  if (!machine.powered || !machine.ready) return;
  const mag = Math.max(2, magnitude - (magnitude % 2)); // keep EVEN (raster parity)
  machine.knob(sign * mag);
  machine.requestSnapshot(); // repositions the live overlay at once when mid-print
}
function attachKnob(btn, sign) {
  let holdTimer = null, repeatTimer = null, ticks = 0;
  const stop = () => {
    if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
    if (repeatTimer) { clearInterval(repeatTimer); repeatTimer = null; }
  };
  btn.addEventListener('pointerdown', (e) => {
    if (btn.disabled) return;
    e.preventDefault();
    try { btn.setPointerCapture(e.pointerId); } catch (_) {}
    const shift = e.shiftKey;
    ticks = 0;
    knobRoll(sign, shift ? KNOB_COARSE_LF : KNOB_FINE_LF); // immediate detent
    holdTimer = setTimeout(() => {                          // then hold-to-repeat
      repeatTimer = setInterval(() => {
        ticks++;
        // Acceleration: fine at first, ramping 2→4→8→…→48 (all even) unless Shift
        // pins it to the coarse line step from the start.
        const mag = shift ? KNOB_COARSE_LF
          : Math.min(KNOB_COARSE_LF, KNOB_FINE_LF * Math.pow(2, (ticks / 3) | 0));
        knobRoll(sign, mag);
      }, 80);
    }, 350);
  });
  for (const ev of ['pointerup', 'pointerleave', 'pointercancel']) {
    btn.addEventListener(ev, stop);
  }
}
attachKnob(el.knobUp, +1);
attachKnob(el.knobDown, -1);
attachKnob(el.knobFeed, +1);
function setKnobEnabled(on) {
  el.knobUp.disabled = !on;
  el.knobDown.disabled = !on;
  el.knobFeed.disabled = !on;
  if (el.printDrawerFeed) el.printDrawerFeed.disabled = !on;
}
if (el.printDrawerFeed) {
  el.printDrawerFeed.addEventListener('click', () => knobRoll(+1, KNOB_COARSE_LF));
}

// Floppy load.
el.loadBtn.addEventListener('click', () => el.floppyFile.click());
el.floppyFile.addEventListener('change', async () => {
  const file = el.floppyFile.files && el.floppyFile.files[0];
  if (!file) return;
  const bytes = new Uint8Array(await file.arrayBuffer());
  const ok = machine.swapFloppy(bytes);
  flashFdd();
  if (!ok) console.warn('[ms2401] floppy swap failed');
  el.floppyFile.value = '';
});

// Floppy save.
el.saveBtn.addEventListener('click', () => {
  const bytes = machine.exportFloppy();
  if (!bytes) { console.warn('[ms2401] floppy export failed'); return; }
  flashFdd();
  const blob = new Blob([bytes], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ms2401-floppy.img';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
});

function flashFdd() {
  el.fddLed.classList.add('is-flash');
  setTimeout(() => el.fddLed.classList.remove('is-flash'), 300);
}

// Beeper mute + volume. Start muted (autoplay-safe).
beeper.setMuted(true);
beeper.setVolume(el.volume.value / 100 * 0.2);
el.muteBtn.addEventListener('click', () => {
  unlockAudio();
  const muted = !beeper.muted;
  beeper.setMuted(muted);
  el.muteBtn.setAttribute('aria-pressed', String(muted));
  el.muteLabel.innerHTML = muted ? '<b>静音</b>' : '<b>响铃</b>';
  el.muteBtn.classList.toggle('is-active', !muted);
});
el.volume.addEventListener('input', () => beeper.setVolume(el.volume.value / 100 * 0.2));

// On-screen keyboard show / hide (default visible; persisted in localStorage).
softkbd.onToggle((visible) => {
  el.kbdBtn.setAttribute('aria-pressed', String(visible));
  el.kbdBtn.classList.toggle('is-active', visible);
  el.kbdBtn.querySelector('.label').innerHTML =
    visible ? '<b>软键盘</b>' : '<b>隐藏键盘</b>';
});
el.kbdBtn.addEventListener('click', () => softkbd.toggle());

// Keyboard capture indicator.
input.onCaptureChange((captured) => {
  el.captureLed.classList.toggle('is-on', captured);
  el.captureLabel.innerHTML = captured ? '<b>键盘捕获</b>' : '<b>键盘释放</b>';
  el.captureHint.classList.toggle('is-hidden', captured);
});

// ---- debug hooks --------------------------------------------------------
window.ms2401 = {
  machine,
  printer,
  pressPrintKey: (ms) => machine.pressPrintKey(ms),
  exportFloppy: () => {
    const b = machine.exportFloppy();
    console.log('[ms2401] floppy export bytes:', b ? b.length : 0);
    return b;
  },
  snapshot: () => machine.requestSnapshot(),
  // 走纸 platen knob: roll paper by delta LF (>0 forward/margin, <0 back/overprint).
  knob: (delta) => machine.knob(delta | 0),
  captureKeyboard: (on = true) => input.setCaptured(on),
  isBooted: () => machine.booted,
  isPowered: () => machine.powered,
  // Front-panel controls.
  powerOff: () => powerDown(),
  powerOn: () => powerUp(),
  reset: () => el.resetBtn.click(),
  setPhosphor: (name) => { el.crtPhosphor.value = name; el.crtPhosphor.dispatchEvent(new Event('change')); },
  setCrisp: (on = true) => crt.setCrisp(!!on),
  enlargeDisplay: (on = true) => setDisplayEnlarged(!!on),
  crt,
  // Tear off + download the strip since the last tear; then the platen rebases.
  tearOff: () => printer.exportPng(),
  openPrintDrawer: (open = true) => {
    el.printDrawer?.classList.toggle('is-open', !!open);
    el.printDrawerToggle?.setAttribute('aria-expanded', String(!!open));
    if (open) updatePrintDrawerPreview();
  },
  // Printer diagnostics.
  platenState: () => printer.debugState(),
  stripInfo: () => printer.stripInfo(),
  // Drive a soft-keyboard key through its real press/hold/release path.
  // softKey('拷屏') or softKey('PRINT') or softKey(10, 3).
  softKey: (labelOrY, x, holdMs) => softkbd.softKey(labelOrY, x, holdMs),
  showKeyboard: (on = true) => softkbd.setVisible(on),
  softkbd,
};

boot();
